"use client"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { extractColorSwatchesFromProducts } from "@/app/(site)/_utils/transformData/extractColorSwatchesFromProducts"
import { useProductStore } from "@/app/(site)/contexts/Global"
import { type ColorSwatch as ColorSwatchType } from "@/app/(site)/contexts/Global/types"
import {
    type RelatedProduct as ContentServiceRelatedProduct,
    type ProductData,
    type SizeInfo
} from "@/lib/content-service/types/ContentServiceProduct"
import { bunnyLoader } from "@/lib/sanityLoader"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { getProductVariantLinkLabel } from "@/src/utils/product/getProductVariantLinkLabel"
import {
    type CheckoutAPIProduct,
    type RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import Image from "next/image"
import { useParams } from "next/navigation"
import React, { useEffect, useMemo } from "react"

interface ColorSwatchProps {
    initialProductValue: (
        | CheckoutAPIProduct
        | RelatedProduct
        | ProductData
        | ContentServiceRelatedProduct
    )[]
    sizes: SizeInfo[]
    currentProduct?: CheckoutAPIProduct | RelatedProduct | ProductData
    langCode: string
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({
    initialProductValue,
    sizes,
    currentProduct,
    langCode
}) => {
    const currentUri = useParams().slug
    const availableSizesContext = useProductStore(state => state.availableSizes)
    const setColorSwatches = useProductStore(state => state.setColorSwatches)
    const selectColorSwatch = useProductStore(state => state.selectColorSwatch)
    const setAvailableSizes = useProductStore(state => state.setAvailableSizes)
    const updateCanAddToCart = useProductStore(state => state.updateCanAddToCart)
    const siteContext = useSiteContext()
    const colorSwatches = useMemo(
        () => extractColorSwatchesFromProducts(initialProductValue),
        [initialProductValue]
    )

    useEffect(() => {
        setColorSwatches(colorSwatches)
        selectColorSwatch(colorSwatches?.[0]?.color_name)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colorSwatches])

    useEffect(() => {
        const updatedSizes = sizes.map(size => {
            const existingSize = availableSizesContext.find(s => s.name === size.name)
            return {
                ...size,
                isSelected: existingSize ? existingSize.isSelected : false
            }
        })

        setAvailableSizes(updatedSizes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChangeVariant = (
        selectedColorSwatch: ColorSwatchType,
        initialSizeValue: SizeInfo[]
    ) => {
        return async () => {
            const selectedSize = availableSizesContext.find(s => s.isSelected)?.name

            if (currentProduct) {
                import("@/lib/utils/tracking/gtmEvents").then(({ pdpChangeSkuEvent }) => {
                    pdpChangeSkuEvent(
                        "change_colour",
                        selectedColorSwatch.color_name,
                        siteContext,
                        currentProduct
                    )
                })
            }

            // Update the isAvailable property of availableSizesContext based on initialSizeValue
            const updatedSizes = availableSizesContext.map(s => {
                const isAvailable =
                    initialSizeValue.find(initialSize => initialSize.name === s.name)
                        ?.isAvailable || s.isAvailable

                return {
                    ...s,
                    isSelected: s.name === selectedSize,
                    isAvailable: isAvailable
                }
            })

            // to indicate color swatch navigation
            localStorage.setItem("navigatingBetweenVariants", "color_swatching")
            // Call the setAvailableSizes function to update the sizes
            setAvailableSizes(updatedSizes)
            selectColorSwatch(selectedColorSwatch.color_name)
            updateCanAddToCart()
        }
    }

    const sortedSwatches = React.useMemo(() => {
        return [...colorSwatches].sort((a, b) => {
            const orderA = a.sortOrder || Infinity
            const orderB = b.sortOrder || Infinity
            return orderA - orderB || (a?.uri || "").localeCompare(b?.uri || "")
        })
    }, [colorSwatches])

    return (
        <div className="flex items-center justify-start gap-x-4 gap-y-8 overflow-x-auto scrollbar-hide lg:flex-wrap">
            {sortedSwatches.map((swatch, index) => {
                const availableThumbnails = swatch.media
                const swatchThumbnailImage = availableThumbnails.find(
                    image => image.attributes?.image_type_usage_type === "swatch" && image
                )
                const selectedThumbnail = swatchThumbnailImage || availableThumbnails[0]

                return (
                    <DynamicLink
                        key={`swatch.uri-${index}`}
                        href={`/product/${swatch.uri}`}
                        passHref
                        scroll={false}
                        shallow
                        prefetch
                        replace
                        onClick={() => {
                            // Set flag to indicate color swatch navigation
                            localStorage.setItem(
                                "navigatingBetweenVariants",
                                "color_swatching"
                            )
                        }}
                        className={clsx("p-4", FOCUS_VISIBLE_INSET_CLASS)}
                        aria-label={getProductVariantLinkLabel(
                            langCode,
                            typeof currentProduct?.name === "string"
                                ? currentProduct.name
                                : "",
                            swatch.color_name
                        )}
                    >
                        <div
                            onClick={() => handleChangeVariant(swatch, sizes)()}
                            className={clsx(
                                "relative aspect-square h-32 w-32",
                                swatch.uri === currentUri &&
                                    "after:absolute after:-bottom-4 after:left-0 after:z-10 after:h-1 after:w-full after:bg-main-black"
                            )}
                        >
                            <Image
                                src={bunnyLoader({
                                    src: selectedThumbnail?.url || "",
                                    width: 60,
                                    quality: 80
                                })}
                                alt=""
                                fill
                                preload
                                className="h-full w-full object-cover object-center"
                                sizes="32px"
                                unoptimized
                            />
                        </div>
                    </DynamicLink>
                )
            })}
        </div>
    )
}

export default ColorSwatch
